import axios from "./axiosConfig";
import React, { createContext, useState, useEffect, useContext } from "react";
import {
	useParams,
	useLocation,
	useNavigate,
	UNSAFE_NavigationContext as NavigationContext,
} from "react-router";
import { SocketContext } from "./socketContext";
import { useAuth } from "./AuthContext";
import { Modal, Button } from "antd";
import datetimeParser from "../components/datetimeParser";

// Create the context
const WplywContext = createContext();

function useBlocker(blocker, when = true) {
	const { navigator } = useContext(NavigationContext);
	const location = useLocation(); // Get the current route

	useEffect(() => {
		if (!when) return;

		const originalPush = navigator.push;
		const originalReplace = navigator.replace;

		// Helper function to normalize the destination path
		const getPath = (destination) => {
			if (typeof destination === "string") {
				return destination; // String path
			}
			if (typeof destination === "object" && destination.pathname) {
				return destination.pathname; // Object with pathname
			}
			return ""; // Fallback
		};

		// Helper function to determine if the blocker should be bypassed
		const shouldBypassBlocker = (currentPath, destinationPath) => {
			// Allow navigation from `/wplywy/nowy` to `/wplywy/:id`
			const isNavigatingFromNowyToId =
				currentPath.startsWith("/wplywy/nowy") &&
				destinationPath.startsWith("/wplywy/") &&
				destinationPath !== "/wplywy/nowy";

			// Allow direct navigation to `/wplywy/nowy`
			const isNavigatingToNowy = destinationPath === "/wplywy/nowy";

			return isNavigatingFromNowyToId || isNavigatingToNowy;
		};

		navigator.push = (...args) => {
			const destinationPath = getPath(args[0]); // Normalize destination path
			const options = args[1] || {}; // Options object

			// Check if navigation should bypass the blocker
			if (
				options.bypassBlocker ||
				shouldBypassBlocker(location.pathname, destinationPath)
			) {
				originalPush(...args); // Directly navigate
			} else {
				blocker(() => originalPush(...args));
			}
		};

		navigator.replace = (...args) => {
			const destinationPath = getPath(args[0]); // Normalize destination path
			const options = args[1] || {};

			if (
				options.bypassBlocker ||
				shouldBypassBlocker(location.pathname, destinationPath)
			) {
				originalReplace(...args);
			} else {
				blocker(() => originalReplace(...args));
			}
		};

		return () => {
			navigator.push = originalPush;
			navigator.replace = originalReplace;
		};
	}, [blocker, navigator, when, location]);
}

// Create a provider component
const WplywProvider = ({ children }) => {
	const socket = useContext(SocketContext);
	const user = useAuth();
	const { wplywid } = useParams();
	const location = useLocation();
	const navigate = useNavigate();

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [pendingNavigation, setPendingNavigation] = useState(null);
	const [blockedModalOpen, setBlockedModalOpen] = useState(false);
	const [blockedInfo, setBlockedInfo] = useState(null);

	const [wplyw, setWplyw] = useState({
		changes: [],
		msr_wplywy_id: wplywid,
		saved: false,
		mode: wplywid === "nowy" ? "edit" : "preview",
		reload: false,
		wplyw_loading: false,
		wplyw_data: {},
	});

	// Update Wplyw ID on URL change
	useEffect(() => {
		setWplyw((prev) => ({ ...prev, msr_wplywy_id: wplywid, saved: false }));
	}, [wplywid]);

	// Log the current wplyw state
	useEffect(() => {
		console.log("Wplyw:", wplyw);
	}, [wplyw]);

	// Handle browser/tab close or refresh
	useEffect(() => {
		const handleBeforeUnload = (event) => {
			if (wplyw.changes?.length > 0) {
				event.preventDefault();
				event.returnValue =
					"Czy na pewno chcesz opuścić stronę? Zmiany mogą zostać utracone.";
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);

		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, [wplyw.changes]);

	// Navigation blocker
	useBlocker((allowNavigation) => {
		if (wplyw.saved || wplyw.changes?.length === 0) {
			// Allow navigation if saved or there are no unsaved changes
			allowNavigation();
		} else {
			setIsModalOpen(true);
			setPendingNavigation(() => allowNavigation);
		}
	}, true);

	const handleModalConfirm = () => {
		setIsModalOpen(false);
		if (pendingNavigation) {
			pendingNavigation(); // Retry the navigation
			setPendingNavigation(null);
		}
	};

	const handleModalCancel = () => {
		setIsModalOpen(false);
		setPendingNavigation(null); // Clear pending navigation
	};

	const handleBlockedModalClose = () => {
		setBlockedModalOpen(false);
		setBlockedInfo(null);
	};

	const takeOverBlockedWplyw = async () => {
		if (!wplyw?.msr_wplywy_id) {
			return;
		}
		try {
			await axios.post("/wplyw/unblock/live", {
				wplywid: wplyw.msr_wplywy_id,
			});
			await axios.post("/wplyw/block/live", {
				wplywid: wplyw.msr_wplywy_id,
				user: user?.user?.user?.mlg_users_id,
			});
			setBlockedModalOpen(false);
			setBlockedInfo(null);
			setWplyw((prev) => ({
				...prev,
				mode: "edit",
				reload: !prev.reload,
			}));
		} catch (error) {
			console.error("Error taking over blocked Wplyw:", error);
		}
	};

	// Fetch data for the Wplyw and block it
	useEffect(() => {
		const requestedWplywId = wplyw.msr_wplywy_id;
		const currentUserId = String(user?.user?.user?.mlg_users_id ?? "");

		if (!requestedWplywId || requestedWplywId === "nowy") {
			setBlockedInfo(null);
			setBlockedModalOpen(false);
		}

		setWplyw((prev) => ({ ...prev, wplyw_loading: true }));

		const fetchData = async () => {
			try {
				const response = await axios.post("/wplyw/get/data", {
					wplywid: requestedWplywId,
				});

				setWplyw((prev) => ({
					...(String(prev.msr_wplywy_id) === String(requestedWplywId)
						? {
								...prev,
								wplyw_loading: false,
								wplyw_data: response.data,
							}
						: prev),
				}));

				if (
					response.data.status &&
					requestedWplywId !== "nowy" &&
					currentUserId
				) {
					const blockedResponse = await axios.post(
						"/wplyw/get/blocked",
						{ wplywid: requestedWplywId },
					);
					const currentlyBlocked = (
						blockedResponse?.data?.data || []
					).find(
						(item) =>
							String(item?.msr_wplywy_msr_wplywy_id) ===
								String(requestedWplywId) &&
							currentUserId &&
							String(item?.msr_blocked_wplywy_user) !==
								currentUserId,
					);

					if (currentlyBlocked) {
						setBlockedInfo(currentlyBlocked);
						setBlockedModalOpen(true);
						setWplyw((prev) =>
							String(prev.msr_wplywy_id) ===
							String(requestedWplywId)
								? { ...prev, mode: "preview" }
								: prev,
						);
						return;
					}

					setBlockedInfo(null);
					setBlockedModalOpen(false);

					await axios.post("/wplyw/block/live", {
						wplywid: requestedWplywId,
						user: currentUserId,
					});
				} else if (
					response.data.status &&
					requestedWplywId !== "nowy"
				) {
					setBlockedInfo(null);
					setBlockedModalOpen(false);
				}
			} catch (error) {
				console.error("Error fetching Wplyw data:", error);
				setWplyw((prev) =>
					String(prev.msr_wplywy_id) === String(requestedWplywId)
						? { ...prev, wplyw_loading: false }
						: prev,
				);
			}
		};

		fetchData();
	}, [user?.user?.user?.mlg_users_id, wplyw.msr_wplywy_id, wplyw.reload]);

	// Handle unblocking of Wplyw on page navigation, tab close, or URL change
	const unblockWplyw = async (wplywid) => {
		try {
			await axios.post("/wplyw/unblock/live", { wplywid });
		} catch (error) {
			console.error("Error unblocking Wplyw:", error);
		}
	};

	// Trigger unblock when URL changes using useLocation
	useEffect(() => {
		return () => {
			if (wplyw.msr_wplywy_id) {
				unblockWplyw(wplyw.msr_wplywy_id);
			}
		};
	}, [location.pathname]);

	return (
		<WplywContext.Provider value={{ wplyw, setWplyw }}>
			<Modal
				title="Masz niezapisane zmiany"
				visible={isModalOpen}
				onOk={handleModalConfirm}
				onCancel={handleModalCancel}
			>
				<p>
					Masz niezapisane zmiany. Czy na pewno chcesz opuścić tę
					stronę?
				</p>
			</Modal>
			<Modal
				title="Wpływ zablokowany przez innego pracownika"
				open={blockedModalOpen}
				onCancel={handleBlockedModalClose}
				footer={null}
			>
				{blockedInfo ? (
					<>
						<p>
							Dany wpis jest już edytowany przez{" "}
							{blockedInfo.user}.
						</p>
						<p>
							Edycja rozpoczeła się{" "}
							{datetimeParser(
								blockedInfo?.msr_blocked_wplywy_datetime,
								"YYYY-MM-DD HH:mm",
							)}
							.
						</p>
						<p>
							Przejęcie edycji może spowodować nadpisywanie zmian.
							Upewnij się, że poprzedni pracownik zakończył pracę.
						</p>
						<div
							style={{
								display: "flex",
								justifyContent: "end",
								gap: 10,
							}}
						>
							<Button onClick={handleBlockedModalClose}>
								Zamknij
							</Button>
							<Button
								danger
								onClick={takeOverBlockedWplyw}
							>
								Przejmij edycję
							</Button>
						</div>
					</>
				) : null}
			</Modal>
			{children}
		</WplywContext.Provider>
	);
};

export { WplywContext, WplywProvider };
